import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart'
import CloseIcon from '@mui/icons-material/Close'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import moment from 'moment'
// Même trame que Abonnement.js / CompteUtilisateur.js : 5 locales chargées pour
// que les libellés de jours suivent la langue active de l'utilisateur.
import 'moment/locale/fr'
import 'moment/locale/de'
import 'moment/locale/it'
import 'moment/locale/es'
import { useTranslation } from 'react-i18next'
import { makeGetRequest, makePostRequest } from 'utils/api'
import { estimateCost } from 'Components/Billing/billingAPI'
import urls from 'utils/urls'

// Liste de courses depuis le menu, en deux étapes.
//
// 1. Repas — on choisit une période (raccourcis ou dates libres), pas
//    seulement la semaine affichée, et on coche les repas.
// 2. Ingrédients — la liste agrégée s'affiche AVANT création : on décoche ce
//    qu'on a déjà et on ajuste les quantités. C'est aussi le seul moment où
//    l'on voit ce que l'IA va recevoir, puisque la création débite des
//    crédits.

const API_DATE = 'DD-MM-YYYY'
const ISO = 'YYYY-MM-DD'
const MEAL_ORDER = ['Matin', 'Midi', 'Après-midi', 'Soir']

const PRESETS = {
  rest: () => [moment(), moment().endOf('isoWeek')],
  next: () => [
    moment().add(1, 'week').startOf('isoWeek'),
    moment().add(1, 'week').endOf('isoWeek'),
  ],
  next7: () => [moment(), moment().add(6, 'days')],
  next14: () => [moment(), moment().add(13, 'days')],
}

// Thème sombre aligné sur GenerateMenuForm (même page, même trame).
const fieldSx = {
  '& .MuiInputBase-root': { color: '#fff' },
  '& .MuiInputBase-input': { color: '#fff' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#7cc6b2' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.22)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.45)' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7cc6b2' },
  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.45)' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' },
  '& input[type=date]::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
}

const sectionTitleSx = {
  color: '#7cc6b2',
  fontSize: 12,
  fontWeight: 900,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
  mb: 1.2,
}

const checkboxSx = {
  color: 'rgba(255,255,255,0.45)',
  '&.Mui-checked': { color: '#7cc6b2' },
  '&.MuiCheckbox-indeterminate': { color: '#7cc6b2' },
}

const mealChipColors = {
  Matin: { bgcolor: 'rgba(245,179,1,0.18)', color: '#f5c451' },
  Midi: { bgcolor: 'rgba(27,117,208,0.22)', color: '#7cb7f0' },
  'Après-midi': { bgcolor: 'rgba(124,198,178,0.18)', color: '#7cc6b2' },
  Soir: { bgcolor: 'rgba(140,110,220,0.22)', color: '#b6a2f0' },
}

const TIERS = [
  { value: 'economic', icon: BoltRoundedIcon, fallbackCost: 1 },
  { value: 'premium', icon: AutoAwesomeRoundedIcon, fallbackCost: 10 },
]

// Les quantités sont éditables : on garde la saisie brute en state (« 1,2 »
// est valide au clavier français) et on ne convertit qu'à l'envoi.
function parseQty(raw) {
  const value = parseFloat(String(raw).replace(',', '.'))
  return Number.isFinite(value) ? value : 0
}

function formatQty(value) {
  const rounded = Math.round((Number(value) || 0) * 100) / 100
  return String(rounded)
}

export default function AddListeCourse({ data, weekStartDate }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { t, i18n: i18nInstance } = useTranslation('layout')
  // Langue active (fr par défaut) pour formater les jours sans dépendre du
  // locale global de moment.
  const rawLang = (i18nInstance.language || 'fr').split('-')[0]
  const lang = ['fr', 'en', 'de', 'it', 'es'].includes(rawLang) ? rawLang : 'fr'

  const [open, setOpen] = useState(false)
  const [step, setStep] = useState('meals')
  const [preset, setPreset] = useState('rest')
  const [startDate, setStartDate] = useState(moment().format(ISO))
  const [endDate, setEndDate] = useState(moment().endOf('isoWeek').format(ISO))
  const [meals, setMeals] = useState([])
  const [selected, setSelected] = useState([])
  const [items, setItems] = useState([])
  const [tier, setTier] = useState('economic')
  const [costs, setCosts] = useState({ economic: 1, premium: 10 })
  const [listName, setListName] = useState('')
  const [loading, setLoading] = useState(false)
  const [aggregating, setAggregating] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  // Période par défaut : le reste de la semaine en cours, sauf si le
  // calendrier affiche une autre semaine — dans ce cas on la reprend.
  const defaultRange = useCallback(() => {
    const displayed = weekStartDate ? moment(weekStartDate, ISO) : null
    if (displayed && displayed.isValid() && !displayed.isSame(moment().startOf('isoWeek'), 'day')) {
      return {
        preset: '',
        from: displayed.format(ISO),
        to: displayed.clone().add(6, 'days').format(ISO),
      }
    }
    const [from, to] = PRESETS.rest()
    return { preset: 'rest', from: from.format(ISO), to: to.format(ISO) }
  }, [weekStartDate])

  const fetchMeals = useCallback(async (from, to) => {
    if (!from || !to || moment(to, ISO).isBefore(moment(from, ISO))) {
      setMeals([])
      setSelected([])
      return
    }
    setLoading(true)
    try {
      const response = await makeGetRequest(
        `${urls.planMealOperations}?limit=200&from=${from}&to=${to}`,
        {},
        true,
      )
      const payload = response?.status === 200 ? response.data : null
      const results = Array.isArray(payload) ? payload : payload?.results || []
      const sorted = [...results].sort((a, b) => {
        const da = moment(a.Date, API_DATE).valueOf()
        const db = moment(b.Date, API_DATE).valueOf()
        if (da !== db) return da - db
        return MEAL_ORDER.indexOf(a.Meal) - MEAL_ORDER.indexOf(b.Meal)
      })
      setMeals(sorted)
      setSelected(sorted.map((m) => m.id))
    } finally {
      setLoading(false)
    }
  }, [])

  // `data` (la semaine affichée) change quand l'utilisateur navigue ou
  // déplace un repas : on rafraîchit la popup si elle est ouverte.
  useEffect(() => {
    if (open) fetchMeals(startDate, endDate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startDate, endDate, data])

  // Coût en crédits de la création (l'IA regroupe et normalise les articles).
  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    Promise.all([
      estimateCost({ feature: 'shopping_list', tier: 'economic' }),
      estimateCost({ feature: 'shopping_list', tier: 'premium' }),
    ]).then(([eco, pro]) => {
      if (cancelled) return
      setCosts({
        economic:
          eco?.status === 200 && typeof eco.data?.cost === 'number' ? eco.data.cost : 1,
        premium:
          pro?.status === 200 && typeof pro.data?.cost === 'number' ? pro.data.cost : 10,
      })
    })
    return () => {
      cancelled = true
    }
  }, [open])

  const handleOpen = () => {
    const range = defaultRange()
    setPreset(range.preset)
    setStartDate(range.from)
    setEndDate(range.to)
    setStep('meals')
    setItems([])
    setListName('')
    setError('')
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setStep('meals')
    setItems([])
    setListName('')
    setError('')
  }

  const applyPreset = (key) => {
    const [from, to] = PRESETS[key]()
    setPreset(key)
    setStartDate(from.format(ISO))
    setEndDate(to.format(ISO))
  }

  const groups = useMemo(() => {
    const byDay = new Map()
    meals.forEach((m) => {
      const iso = moment(m.Date, API_DATE).format(ISO)
      if (!byDay.has(iso)) byDay.set(iso, [])
      byDay.get(iso).push(m)
    })
    return [...byDay.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([iso, rows]) => ({ iso, dayMeals: rows }))
  }, [meals])

  const toggleMeal = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id],
    )
  }

  const toggleDay = (dayMeals) => {
    const ids = dayMeals.map((m) => m.id)
    const allOn = ids.every((id) => selected.includes(id))
    setSelected((prev) =>
      allOn ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])],
    )
  }

  const defaultListName = t('planMeal.shoppingList.defaultName', {
    date: moment().format('DD/MM/YYYY'),
  })

  // ---- Étape 1 → 2 : agrégation, sans aucun débit de crédit -------------
  const goToReview = async () => {
    if (selected.length === 0) {
      setError(t('planMeal.shoppingList.errorNoSelection'))
      return
    }

    setAggregating(true)
    setError('')

    try {
      const response = await makePostRequest(urls.getIngredients, { selected }, {}, true)

      if (!response || response.status !== 200) {
        // On remonte le message du backend quand il y en a un : un message
        // générique ne dit rien de ce qui a réellement échoué.
        const detail = response?.data?.detail || response?.data?.message || ''
        throw new Error(
          detail
            ? `${t('planMeal.shoppingList.errorIngredients')} : ${detail}`
            : t('planMeal.shoppingList.errorIngredients'),
        )
      }

      const rows = Array.isArray(response.data) ? response.data : []
      setItems(
        rows.map((row, index) => ({
          key: row.id != null ? `t${row.id}` : `i${index}`,
          name: row.name || row.NomIngredient || '',
          qty: formatQty(row.qty),
          unit: row.unit?.Designation || row.unit?.UM || '',
          rayon: (row.rayon || '').trim(),
          recipes: Array.isArray(row.recipes) ? row.recipes : [],
          keep: true,
        })),
      )
      setStep('review')
    } catch (err) {
      console.error('Error aggregating ingredients:', err)
      setError(err.message || t('planMeal.shoppingList.errorIngredients'))
    } finally {
      setAggregating(false)
    }
  }

  // ---- Étape 2 : création (c'est ICI que les crédits sont débités) ------
  const createList = async () => {
    const kept = items.filter((item) => item.keep && item.name)
    if (kept.length === 0) {
      setError(t('planMeal.shoppingList.errorNoItemKept'))
      return
    }

    setCreating(true)
    setError('')

    try {
      const finalListName = listName.trim() || defaultListName

      // Le backend renvoie `qty` / `unit` (TempIngredientSerializer). L'ancien
      // mapping cherchait `Quantite` / `um` : toutes les quantités tombaient
      // donc à 1 et l'unité à « pcs », quelle que soit la recette.
      const selectedIngredientsData = kept.map((item) => ({
        name: item.name,
        quantity: parseQty(item.qty) || 1,
        unit: item.unit || '',
        notes: item.recipes.length ? `Recettes: ${item.recipes.join(', ')}` : '',
      }))

      const response = await makePostRequest(
        `${urls.shoppingLists}create_from_recipes/`,
        {
          recipe_ids: selected,
          selected_ingredients: selectedIngredientsData,
          list_name: finalListName,
          tier,
        },
        {},
        true,
      )

      if (response?.status === 201) {
        handleClose()
        alert(
          t('planMeal.shoppingList.success', {
            name: finalListName,
            count: selectedIngredientsData.length,
          }),
        )
      } else {
        const detail = response?.data?.detail || response?.data?.message || ''
        throw new Error(
          detail
            ? `${t('planMeal.shoppingList.errorCreate')} : ${detail}`
            : t('planMeal.shoppingList.errorCreate'),
        )
      }
    } catch (err) {
      console.error('Error creating shopping list:', err)
      setError(err.message || t('planMeal.shoppingList.errorCreate'))
    } finally {
      setCreating(false)
    }
  }

  const onSubmit = (event) => {
    event.preventDefault()
    if (step === 'meals') goToReview()
    else createList()
  }

  // ---- Étape 2 : sélection et regroupement ------------------------------
  const toggleItem = (key) => {
    setItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, keep: !item.keep } : item)),
    )
  }

  const setItemQty = (key, value) => {
    setItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, qty: value } : item)),
    )
  }

  const setItemsKeep = (keys, keep) => {
    const target = new Set(keys)
    setItems((prev) =>
      prev.map((item) => (target.has(item.key) ? { ...item, keep } : item)),
    )
  }

  // Regroupement par rayon. Le champ `Rayon` de la fiche ingrédient n'est pas
  // toujours rempli : si aucun article n'en a, on retombe sur une simple liste
  // alphabétique plutôt que d'afficher un unique bandeau « Sans rayon ».
  const itemGroups = useMemo(() => {
    const sorted = [...items].sort((a, b) =>
      a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }),
    )
    if (!sorted.some((item) => item.rayon)) {
      return [{ rayon: '', flat: true, items: sorted }]
    }
    const byRayon = new Map()
    sorted.forEach((item) => {
      const key = item.rayon || ''
      if (!byRayon.has(key)) byRayon.set(key, [])
      byRayon.get(key).push(item)
    })
    return [...byRayon.entries()]
      .sort((a, b) => {
        if (!a[0]) return 1 // « Sans rayon » toujours en dernier
        if (!b[0]) return -1
        return a[0].localeCompare(b[0], 'fr', { sensitivity: 'base' })
      })
      .map(([rayon, rows]) => ({ rayon, flat: false, items: rows }))
  }, [items])

  const keptCount = items.filter((item) => item.keep).length

  const rangeLabel = {
    from: moment(startDate, ISO).locale(lang).format('ddd D MMM'),
    to: moment(endDate, ISO).locale(lang).format('ddd D MMM'),
  }

  return (
    <>
      {isMobile ? (
        <AddShoppingCartIcon onClick={handleOpen} />
      ) : (
        <Button
          variant="outlined"
          startIcon={<AddShoppingCartIcon />}
          onClick={handleOpen}
          sx={{
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 850,
            px: 1.8,
            color: '#ffffff',
            borderColor: 'rgba(255,255,255,0.22)',
            '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.08)' },
          }}
        >
          {t('planMeal.shoppingList.trigger')}
        </Button>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        PaperProps={{
          sx: {
            bgcolor: '#0a1130',
            backgroundImage: 'linear-gradient(180deg, #0d1838 0%, #0a1130 100%)',
            color: '#fff',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.1)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" gap={1}>
              <AddShoppingCartIcon sx={{ color: '#7cc6b2' }} />
              <Typography sx={{ fontWeight: 950, fontSize: 20 }}>
                {t('planMeal.shoppingList.title')}
              </Typography>
            </Stack>
            <IconButton onClick={handleClose} size="small" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, mt: 0.5 }}>
            {step === 'meals'
              ? t('planMeal.shoppingList.subtitle')
              : t('planMeal.shoppingList.subtitleReview')}
          </Typography>
          <Stack direction="row" alignItems="center" gap={1.2} sx={{ mt: 1.4 }}>
            {[
              ['meals', t('planMeal.shoppingList.stepMeals')],
              ['review', t('planMeal.shoppingList.stepItems')],
            ].map(([key, label], index) => {
              const isCurrent = step === key
              const isDone = key === 'meals' && step === 'review'
              return (
                <Stack key={key} direction="row" alignItems="center" gap={1.2} flex={index === 0 ? 1 : 'none'}>
                  <Stack direction="row" alignItems="center" gap={0.8}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 11,
                        fontWeight: 900,
                        bgcolor: isCurrent
                          ? '#7cc6b2'
                          : isDone
                          ? 'rgba(124,198,178,0.22)'
                          : 'rgba(255,255,255,0.10)',
                        color: isCurrent
                          ? '#062018'
                          : isDone
                          ? '#7cc6b2'
                          : 'rgba(255,255,255,0.42)',
                      }}
                    >
                      {isDone ? '✓' : index + 1}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        fontWeight: 800,
                        color: isCurrent
                          ? '#fff'
                          : isDone
                          ? '#7cc6b2'
                          : 'rgba(255,255,255,0.42)',
                      }}
                    >
                      {label}
                    </Typography>
                  </Stack>
                  {index === 0 && (
                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.1)' }} />
                  )}
                </Stack>
              )
            })}
          </Stack>
        </DialogTitle>

        <form onSubmit={onSubmit}>
          <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
            {step === 'meals' && (
              <>
            {/* Période -------------------------------------------------- */}
            <Typography sx={sectionTitleSx}>
              {t('planMeal.shoppingList.sectionPeriod')}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.8} sx={{ mb: 1.6 }}>
              {[
                ['rest', t('planMeal.shoppingList.presetRestOfWeek')],
                ['next', t('planMeal.shoppingList.presetNextWeek')],
                ['next7', t('planMeal.shoppingList.presetNext7')],
                ['next14', t('planMeal.shoppingList.presetNext14')],
              ].map(([key, label]) => (
                <Chip
                  key={key}
                  label={label}
                  onClick={() => applyPreset(key)}
                  variant={preset === key ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: 800,
                    color: '#fff',
                    bgcolor: preset === key ? 'rgba(124,198,178,0.28)' : 'transparent',
                    borderColor: 'rgba(255,255,255,0.25)',
                  }}
                />
              ))}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t('planMeal.shoppingList.fieldFrom')}
                value={startDate}
                onChange={(e) => {
                  setPreset('')
                  setStartDate(e.target.value)
                }}
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t('planMeal.shoppingList.fieldTo')}
                value={endDate}
                onChange={(e) => {
                  setPreset('')
                  setEndDate(e.target.value)
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDate }}
                sx={fieldSx}
              />
            </Stack>
            <Box
              sx={{
                mt: 1.4,
                px: 1.4,
                py: 1,
                borderRadius: 2,
                bgcolor: 'rgba(124,198,178,0.12)',
                color: '#a9dccd',
                fontSize: 12.5,
                fontWeight: 700,
              }}
            >
              {loading
                ? t('planMeal.shoppingList.loading')
                : t(
                    meals.length
                      ? 'planMeal.shoppingList.recap'
                      : 'planMeal.shoppingList.recapEmpty',
                    { count: meals.length, ...rangeLabel },
                  )}
            </Box>

            {/* Nom de la liste ------------------------------------------ */}
            <Typography sx={{ ...sectionTitleSx, mt: 3 }}>
              {t('planMeal.shoppingList.sectionName')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder={defaultListName}
              helperText={t('planMeal.shoppingList.fieldNameHelper')}
              disabled={creating}
              sx={fieldSx}
            />

            {/* Repas ---------------------------------------------------- */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 3, mb: 1.2 }}
            >
              <Typography sx={{ ...sectionTitleSx, mb: 0 }}>
                {t('planMeal.shoppingList.sectionMeals')}
              </Typography>
              <Stack direction="row" gap={0.5}>
                <Button
                  size="small"
                  onClick={() => setSelected(meals.map((m) => m.id))}
                  sx={{ color: '#7cc6b2', textTransform: 'none', fontWeight: 800, fontSize: 12.5 }}
                >
                  {t('planMeal.shoppingList.selectAll')}
                </Button>
                <Button
                  size="small"
                  onClick={() => setSelected([])}
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: 12.5,
                  }}
                >
                  {t('planMeal.shoppingList.selectNone')}
                </Button>
              </Stack>
            </Stack>

            {loading && (
              <Stack alignItems="center" sx={{ py: 3 }}>
                <CircularProgress size={26} sx={{ color: '#7cc6b2' }} />
              </Stack>
            )}

            {!loading && groups.length === 0 && (
              <Box
                sx={{
                  border: '1px dashed rgba(255,255,255,0.2)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: 14, mb: 0.5 }}>
                  {t('planMeal.shoppingList.emptyTitle')}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                  {t('planMeal.shoppingList.emptyHint')}
                </Typography>
              </Box>
            )}

            {!loading &&
              groups.map(({ iso, dayMeals }) => {
                const ids = dayMeals.map((m) => m.id)
                const checkedCount = ids.filter((id) => selected.includes(id)).length
                return (
                  <Box
                    key={iso}
                    sx={{
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 2,
                      mb: 1.2,
                      overflow: 'hidden',
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={0.5}
                      onClick={() => toggleDay(dayMeals)}
                      sx={{
                        px: 1,
                        py: 0.6,
                        bgcolor: 'rgba(255,255,255,0.05)',
                        cursor: 'pointer',
                      }}
                    >
                      <Checkbox
                        size="small"
                        sx={checkboxSx}
                        checked={checkedCount === ids.length}
                        indeterminate={checkedCount > 0 && checkedCount < ids.length}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleDay(dayMeals)}
                      />
                      <Typography
                        sx={{ fontWeight: 800, fontSize: 13, textTransform: 'capitalize' }}
                      >
                        {moment(iso, ISO).locale(lang).format('dddd D MMMM')}
                      </Typography>
                      <Typography
                        sx={{
                          ml: 'auto',
                          mr: 1,
                          fontSize: 11.5,
                          color: 'rgba(255,255,255,0.45)',
                          fontWeight: 700,
                        }}
                      >
                        {t('planMeal.shoppingList.dayCount', { count: dayMeals.length })}
                      </Typography>
                    </Stack>

                    {dayMeals.map((m) => (
                      <Stack
                        key={m.id}
                        direction="row"
                        alignItems="center"
                        gap={0.8}
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderTop: '1px solid rgba(255,255,255,0.07)',
                        }}
                      >
                        <Checkbox
                          size="small"
                          sx={checkboxSx}
                          checked={selected.includes(m.id)}
                          onChange={() => toggleMeal(m.id)}
                          disabled={creating}
                        />
                        <Chip
                          size="small"
                          label={m.Meal}
                          sx={{
                            height: 20,
                            fontSize: 10.5,
                            fontWeight: 900,
                            ...(mealChipColors[m.Meal] || {
                              bgcolor: 'rgba(255,255,255,0.1)',
                              color: '#fff',
                            }),
                          }}
                        />
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                          {m.RecetteNom || t('planMeal.shoppingList.noRecipe')}
                        </Typography>
                        <Stack direction="row" alignItems="center" gap={0.8} sx={{ ml: 'auto' }}>
                          {m.ingredients_count === 0 && (
                            <Chip
                              size="small"
                              icon={<WarningAmberIcon sx={{ fontSize: 13 }} />}
                              label={t('planMeal.shoppingList.noIngredients')}
                              sx={{
                                height: 20,
                                fontSize: 10.5,
                                fontWeight: 800,
                                bgcolor: 'rgba(245,179,1,0.16)',
                                color: '#f5c451',
                                '& .MuiChip-icon': { color: '#f5c451' },
                              }}
                            />
                          )}
                          <Typography
                            sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.45)', fontWeight: 700 }}
                          >
                            {t('planMeal.shoppingList.servings', { count: m.Servir || 0 })}
                          </Typography>
                        </Stack>
                      </Stack>
                    ))}
                  </Box>
                )
              })}
              </>
            )}

            {/* Étape 2 : revue des ingrédients agrégés ------------------- */}
            {step === 'review' && (
              <>
                <Box
                  sx={{
                    px: 1.4,
                    py: 1,
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: 'rgba(124,198,178,0.12)',
                    color: '#a9dccd',
                    fontSize: 12.5,
                    fontWeight: 700,
                  }}
                >
                  {t('planMeal.shoppingList.reviewRecap', {
                    meals: selected.length,
                    items: items.length,
                  })}
                </Box>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1.2 }}
                >
                  <Typography sx={{ ...sectionTitleSx, mb: 0 }}>
                    {t('planMeal.shoppingList.sectionItems')}
                  </Typography>
                  <Stack direction="row" gap={0.5}>
                    <Button
                      size="small"
                      onClick={() => setItemsKeep(items.map((i) => i.key), true)}
                      sx={{ color: '#7cc6b2', textTransform: 'none', fontWeight: 800, fontSize: 12.5 }}
                    >
                      {t('planMeal.shoppingList.selectAll')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setItemsKeep(items.map((i) => i.key), false)}
                      sx={{
                        color: 'rgba(255,255,255,0.6)',
                        textTransform: 'none',
                        fontWeight: 800,
                        fontSize: 12.5,
                      }}
                    >
                      {t('planMeal.shoppingList.selectNone')}
                    </Button>
                  </Stack>
                </Stack>

                {items.length === 0 && (
                  <Box
                    sx={{
                      border: '1px dashed rgba(255,255,255,0.2)',
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: 14, mb: 0.5 }}>
                      {t('planMeal.shoppingList.noItemsTitle')}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                      {t('planMeal.shoppingList.noItemsHint')}
                    </Typography>
                  </Box>
                )}

                {itemGroups.map((group) => {
                  const keys = group.items.map((i) => i.key)
                  const checkedCount = group.items.filter((i) => i.keep).length
                  return (
                    <Box
                      key={group.rayon || '__none__'}
                      sx={{
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 2,
                        mb: 1.2,
                        overflow: 'hidden',
                      }}
                    >
                      {!group.flat && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          gap={0.5}
                          onClick={() => setItemsKeep(keys, checkedCount !== keys.length)}
                          sx={{
                            px: 1,
                            py: 0.6,
                            bgcolor: 'rgba(255,255,255,0.05)',
                            cursor: 'pointer',
                          }}
                        >
                          <Checkbox
                            size="small"
                            sx={checkboxSx}
                            checked={checkedCount === keys.length}
                            indeterminate={checkedCount > 0 && checkedCount < keys.length}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => setItemsKeep(keys, checkedCount !== keys.length)}
                          />
                          <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
                            {group.rayon || t('planMeal.shoppingList.noAisle')}
                          </Typography>
                          <Typography
                            sx={{
                              ml: 'auto',
                              mr: 1,
                              fontSize: 11.5,
                              color: 'rgba(255,255,255,0.45)',
                              fontWeight: 700,
                            }}
                          >
                            {t('planMeal.shoppingList.aisleCount', { count: keys.length })}
                          </Typography>
                        </Stack>
                      )}

                      {group.items.map((item) => (
                        <Stack
                          key={item.key}
                          direction="row"
                          alignItems="center"
                          gap={0.8}
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderTop: group.flat ? 'none' : '1px solid rgba(255,255,255,0.07)',
                            '&:not(:first-of-type)': {
                              borderTop: '1px solid rgba(255,255,255,0.07)',
                            },
                          }}
                        >
                          <Checkbox
                            size="small"
                            sx={checkboxSx}
                            checked={item.keep}
                            onChange={() => toggleItem(item.key)}
                            disabled={creating}
                          />
                          <Box sx={{ minWidth: 0, flex: 1, opacity: item.keep ? 1 : 0.4 }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 650, lineHeight: 1.25 }}>
                              {item.name}
                            </Typography>
                            {item.recipes.length > 0 && (
                              <Typography
                                noWrap
                                sx={{ fontSize: 11, color: 'rgba(255,255,255,0.42)', mt: 0.2 }}
                              >
                                {item.recipes.join(', ')}
                              </Typography>
                            )}
                          </Box>
                          <TextField
                            size="small"
                            value={item.qty}
                            onChange={(e) => setItemQty(item.key, e.target.value)}
                            disabled={creating || !item.keep}
                            inputProps={{
                              inputMode: 'decimal',
                              style: { textAlign: 'right', padding: '5px 8px' },
                            }}
                            sx={{ ...fieldSx, width: 76 }}
                          />
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: 'rgba(255,255,255,0.6)',
                              fontWeight: 700,
                              minWidth: 42,
                            }}
                          >
                            {item.unit || '—'}
                          </Typography>
                        </Stack>
                      ))}
                    </Box>
                  )
                })}

                {/* Nom de la liste --------------------------------------- */}
                <Typography sx={{ ...sectionTitleSx, mt: 3 }}>
                  {t('planMeal.shoppingList.sectionName')}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={listName}
                  onChange={(e) => setListName(e.target.value)}
                  placeholder={defaultListName}
                  helperText={t('planMeal.shoppingList.fieldNameHelper')}
                  disabled={creating}
                  sx={fieldSx}
                />

                {/* Modèle IA -------------------------------------------- */}
                <Typography sx={{ ...sectionTitleSx, mt: 3 }}>
                  {t('planMeal.shoppingList.sectionModel')}
                </Typography>
                <Stack gap={1}>
                  {TIERS.map(({ value, icon: Icon }) => {
                    const isOn = tier === value
                    return (
                      <Stack
                        key={value}
                        direction="row"
                        alignItems="center"
                        gap={1.2}
                        onClick={() => !creating && setTier(value)}
                        sx={{
                          px: 1.4,
                          py: 1,
                          borderRadius: 2,
                          cursor: creating ? 'default' : 'pointer',
                          border: `1px solid ${isOn ? '#7cc6b2' : 'rgba(255,255,255,0.14)'}`,
                          bgcolor: isOn ? 'rgba(124,198,178,0.10)' : 'transparent',
                        }}
                      >
                        <Icon sx={{ fontSize: 18, color: isOn ? '#7cc6b2' : 'rgba(255,255,255,0.5)' }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                            {t(`planMeal.shoppingList.tier_${value}`)}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.42)' }}>
                            {t(`planMeal.shoppingList.tier_${value}_hint`)}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={t('planMeal.shoppingList.credits', { count: costs[value] })}
                          sx={{
                            height: 20,
                            fontSize: 10.5,
                            fontWeight: 900,
                            bgcolor: isOn ? 'rgba(124,198,178,0.22)' : 'rgba(255,255,255,0.08)',
                            color: isOn ? '#7cc6b2' : 'rgba(255,255,255,0.6)',
                          }}
                        />
                      </Stack>
                    )
                  })}
                </Stack>
              </>
            )}

            {error && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Typography
              sx={{ mr: 'auto', fontSize: 12.5, color: 'rgba(255,255,255,0.6)', fontWeight: 700 }}
            >
              {step === 'meals'
                ? t('planMeal.shoppingList.footerCount', { count: selected.length })
                : t('planMeal.shoppingList.footerKept', {
                    count: keptCount,
                    total: items.length,
                  })}
            </Typography>
            {step === 'review' && (
              <Button
                onClick={() => setStep('meals')}
                disabled={creating}
                sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none', fontWeight: 800 }}
              >
                {t('planMeal.shoppingList.btnBack')}
              </Button>
            )}
            <Button
              onClick={handleClose}
              disabled={creating}
              sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none', fontWeight: 800 }}
            >
              {t('planMeal.shoppingList.btnCancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                step === 'meals'
                  ? aggregating || selected.length === 0
                  : creating || keptCount === 0
              }
              startIcon={
                aggregating || creating ? (
                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                ) : null
              }
              sx={{
                bgcolor: '#1b75d0',
                textTransform: 'none',
                fontWeight: 900,
                borderRadius: 999,
                px: 2.4,
                '&:hover': { bgcolor: '#145da8' },
                '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.4)' },
              }}
            >
              {step === 'meals'
                ? aggregating
                  ? t('planMeal.shoppingList.aggregating')
                  : t('planMeal.shoppingList.btnNext')
                : creating
                  ? t('planMeal.shoppingList.creating')
                  : t('planMeal.shoppingList.btnCreate')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}
